import React from 'react'
import { Card, Progress, Tag, Icon, Tooltip, Badge } from 'antd'
import api from '../../utils/api'

class CvQueueStatus extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      queueStatus: null,
      activeJobProgress: {}, // jobId -> progress
      error: null,
    }
    this.pollInterval = null
    this.sseCleanups = {}
  }

  componentDidMount() {
    this.fetchQueueStatus()
    // Poll every 5 seconds
    this.pollInterval = setInterval(() => this.fetchQueueStatus(), 5000)
  }

  componentWillUnmount() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
    // Cleanup SSE connections
    Object.values(this.sseCleanups).forEach(cleanup => cleanup && cleanup())
  }

  async fetchQueueStatus() {
    try {
      const queueStatus = await api.getCvQueueStatus()
      this.setState({ queueStatus, loading: false, error: null })
      
      // Subscribe to SSE for active jobs
      queueStatus.activeJobs.forEach(job => {
        if (!this.sseCleanups[job.jobId]) {
          this.subscribeToJob(job.jobId)
        }
      })
    } catch (err) {
      this.setState({ error: err.message, loading: false })
    }
  }

  subscribeToJob(jobId) {
    const cleanup = api.subscribeToCvJobProgress(
      jobId,
      (progress) => {
        this.setState(state => ({
          activeJobProgress: {
            ...state.activeJobProgress,
            [jobId]: progress,
          },
        }))
      },
      () => {
        // On error, remove cleanup
        delete this.sseCleanups[jobId]
      },
      () => {
        // On complete, cleanup and refresh
        delete this.sseCleanups[jobId]
        this.fetchQueueStatus()
      }
    )
    this.sseCleanups[jobId] = cleanup
  }

  renderJobProgress(job) {
    const progress = this.state.activeJobProgress[job.jobId] || job
    const isBulk = job.type === 'bulk'
    
    if (isBulk) {
      const percent = progress.totalCvs > 0 
        ? Math.round((progress.completedCvs / progress.totalCvs) * 100)
        : 0
      return (
        <div key={job.jobId} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span>
              <Tag color="blue">Bulk</Tag>
              <strong>{progress.currentMember}</strong>
            </span>
            <span style={{ color: '#888' }}>
              {progress.completedCvs}/{progress.totalCvs} CVs
              {progress.estimatedRemaining && ` • ETA: ${progress.estimatedRemaining}`}
            </span>
          </div>
          <Progress 
            percent={percent} 
            size="small" 
            status={progress.status === 'processing' ? 'active' : 'success'}
          />
          {progress.currentCv && (
            <div style={{ fontSize: 12, color: '#888' }}>{progress.currentCv}</div>
          )}
        </div>
      )
    }
    
    // Single member job
    const percent = progress.total > 0 
      ? Math.round((progress.completed / progress.total) * 100)
      : 0
    return (
      <div key={job.jobId} style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>
            <Tag color="green">Single</Tag>
            <strong>{progress.memberSnag}</strong>
          </span>
          <span style={{ color: '#888' }}>
            {progress.completed}/{progress.total} CVs
          </span>
        </div>
        <Progress 
          percent={percent} 
          size="small" 
          status={progress.status === 'processing' ? 'active' : 'success'}
        />
        {progress.current && (
          <div style={{ fontSize: 12, color: '#888' }}>{progress.current}</div>
        )}
      </div>
    )
  }

  render() {
    const { queueStatus, loading, error } = this.state
    
    // Don't show anything if no active jobs and storage is configured
    if (!loading && queueStatus && 
        queueStatus.activeJobs.length === 0 && 
        queueStatus.pendingJobs.length === 0 &&
        queueStatus.storageConfigured) {
      return null
    }

    // Show warning if storage not configured
    if (!loading && queueStatus && !queueStatus.storageConfigured) {
      return (
        <Card 
          size="small" 
          style={{ marginBottom: 16, backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}
        >
          <Icon type="warning" style={{ color: '#faad14', marginRight: 8 }} />
          <span style={{ color: '#ad6800' }}>
            CV storage (S3/R2) is not configured. CVs will not be pre-generated.
          </span>
        </Card>
      )
    }

    // Show error
    if (error) {
      return (
        <Card 
          size="small" 
          style={{ marginBottom: 16, backgroundColor: '#fff2f0', borderColor: '#ffccc7' }}
        >
          <Icon type="exclamation-circle" style={{ color: '#ff4d4f', marginRight: 8 }} />
          <span style={{ color: '#cf1322' }}>Error loading queue status: {error}</span>
        </Card>
      )
    }

    // Loading or no queue status
    if (loading || !queueStatus) {
      return null
    }

    const { activeJobs, pendingJobs } = queueStatus
    const totalJobs = activeJobs.length + pendingJobs.length

    if (totalJobs === 0) {
      return null
    }

    return (
      <Card 
        size="small" 
        style={{ marginBottom: 16 }}
        title={
          <span>
            <Icon type="sync" spin={activeJobs.length > 0} style={{ marginRight: 8 }} />
            CV Generation Queue
            <Badge 
              count={totalJobs} 
              style={{ marginLeft: 8, backgroundColor: activeJobs.length > 0 ? '#1890ff' : '#d9d9d9' }}
            />
          </span>
        }
        extra={
          <Tooltip title="CVs are being generated in the background">
            <Icon type="info-circle" style={{ color: '#888' }} />
          </Tooltip>
        }
      >
        {/* Active Jobs */}
        {activeJobs.length > 0 && (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              <Icon type="loading" style={{ marginRight: 4 }} /> Processing
            </div>
            {activeJobs.map(job => this.renderJobProgress(job))}
          </div>
        )}
        
        {/* Pending Jobs */}
        {pendingJobs.length > 0 && (
          <div style={{ marginTop: activeJobs.length > 0 ? 16 : 0 }}>
            <div style={{ marginBottom: 8, fontWeight: 500, color: '#888' }}>
              <Icon type="clock-circle" style={{ marginRight: 4 }} /> Queued ({pendingJobs.length})
            </div>
            {pendingJobs.slice(0, 3).map(job => (
              <div key={job.jobId} style={{ marginBottom: 4 }}>
                <Tag color={job.type === 'bulk' ? 'blue' : 'green'}>
                  {job.type === 'bulk' ? 'Bulk' : 'Single'}
                </Tag>
                <span>{job.type === 'bulk' ? `${job.totalMembers} members` : job.memberSnag}</span>
              </div>
            ))}
            {pendingJobs.length > 3 && (
              <div style={{ color: '#888', fontSize: 12 }}>
                ...and {pendingJobs.length - 3} more
              </div>
            )}
          </div>
        )}
      </Card>
    )
  }
}

export default CvQueueStatus
